// Generic DM reference library page: built-in cards extracted from the old
// site plus custom entries the DM adds (stored in layout.referenceEntries[page]).
import { useMemo, useState } from "react";
import reference from "../content/reference.json";
import { useStore } from "../state/store";

export interface LibraryEntry { id?: string; title: string; badge?: string; text: string; rows: string[]; custom?: boolean }
type Pages = Record<string, LibraryEntry[]>;
const BUILTIN = (reference as any).libraryPages as Pages;

export function useCustomEntries(page: string) {
  const { layout, update } = useStore();
  const all = (layout.referenceEntries || {}) as Pages;
  const entries = all[page] || [];
  const save = (next: LibraryEntry[]) => update((l) => ({ ...l, referenceEntries: { ...((l.referenceEntries as Pages) || {}), [page]: next } }));
  return { entries, save };
}

export function EntryCard({ entry, onEdit, onRemove }: { entry: LibraryEntry; onEdit?: () => void; onRemove?: () => void }) {
  return (
    <article className="ref-card">
      <header><strong>{entry.title}</strong>{entry.badge && <span className="pill">{entry.badge}</span>}{entry.custom && <span className="pill muted">custom</span>}
        {onEdit && <button className="link small" onClick={onEdit}>edit</button>}{onRemove && <button className="link small" onClick={onRemove}>remove</button>}</header>
      {entry.text && <p>{entry.text}</p>}
      {entry.rows?.length ? <ul>{entry.rows.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
    </article>
  );
}

export function EntryEditor({ initial, onSave, onCancel }: { initial?: LibraryEntry; onSave: (e: LibraryEntry) => void; onCancel: () => void }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [badge, setBadge] = useState(initial?.badge || "");
  const [text, setText] = useState(initial?.text || "");
  const [rows, setRows] = useState((initial?.rows || []).join("\n"));
  return (
    <form className="ref-editor" onSubmit={(e) => { e.preventDefault(); if (!title.trim()) return; onSave({ ...(initial || {}), id: initial?.id || `ref-${Date.now().toString(36)}`, title: title.trim(), badge: badge.trim(), text: text.trim(), rows: rows.split("\n").map((r) => r.trim()).filter(Boolean), custom: true }); }}>
      <div className="row"><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required aria-label="Title" /><input value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="Badge (optional)" aria-label="Badge" /></div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Summary" rows={2} aria-label="Summary" />
      <textarea value={rows} onChange={(e) => setRows(e.target.value)} placeholder="One line per bullet (or one result per line for a random table)" rows={5} aria-label="Lines" />
      <div className="row"><span className="spacer" /><button type="button" className="ghost" onClick={onCancel}>Cancel</button><button type="submit" className="primary">Save entry</button></div>
    </form>
  );
}

export function ReferenceLibrary({ page, eyebrow = "DM reference library", intro = "Campaign-ready rules kept within reach during play.", children }: { page: string; eyebrow?: string; intro?: string; children?: React.ReactNode }) {
  const { entries, save } = useCustomEntries(page);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<LibraryEntry | "new" | null>(null);
  const builtin = BUILTIN[page] || [];
  const all = useMemo(() => [...entries.map((e) => ({ ...e, custom: true })), ...builtin], [entries, builtin]);
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((e) => `${e.title} ${e.badge || ""} ${e.text} ${(e.rows || []).join(" ")}`.toLowerCase().includes(q)) : all;

  return (
    <section className="panel library">
      <header className="panel-head">
        <div><span className="eyebrow">{eyebrow}</span><h1>{page}</h1><p className="muted">{intro}</p></div>
        <div className="actions"><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="⌕ Search" aria-label={`Search ${page}`} /><button className="primary" onClick={() => setEditing("new")}>＋ Add entry</button></div>
      </header>
      {children}
      {editing && <EntryEditor initial={editing === "new" ? undefined : editing} onCancel={() => setEditing(null)} onSave={(e) => { save(editing === "new" ? [e, ...entries] : entries.map((x) => (x.id === e.id ? e : x))); setEditing(null); }} />}
      <div className="ref-grid">
        {shown.map((e, i) => <EntryCard key={e.id || `${e.title}-${i}`} entry={e} onEdit={e.custom ? () => setEditing(e) : undefined} onRemove={e.custom ? () => save(entries.filter((x) => x.id !== e.id)) : undefined} />)}
        {!shown.length && <p className="empty">{q ? "No entries match." : "No entries yet. Add one to keep it within reach at the table."}</p>}
      </div>
    </section>
  );
}
